// Display formatting shared by the League Play landing cards. One place, so a
// prize or a date range reads identically on every tile and breakpoint.

// "1350.00" → "$1,350"  ·  "0"/""/null → null (caller decides what to show)
export const formatPrize = (amount) => {
  if (amount === null || amount === undefined || amount === "") return null;
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const whole = Math.round(n * 100) % 100 === 0;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`;
};

const monthDay = (date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

// "Oct 1, 2025" — the same month/day/year treatment the create wizard's review
// step already uses, so a year reads the same wherever the app has to print one.
const monthDayYear = (date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const isCurrentYear = (date, now) => date.getFullYear() === now.getFullYear();

// THE date rule for the whole product. One place, so a league's dates are
// spelled the same on a card, on a landing page and in the create wizard.
//
// Client: "I don't think including the year is necessary until we are no longer
// in 2026. The year might take up too much space, especially on mobile." — so
// the year appears only when the date is NOT in the current year, where leaving
// it off would be genuinely ambiguous rather than merely terse.
//
// Said in the same breath as "Stick to our format with dates. Don't let us drift
// because of the stitch design drifting", which is why this is exported and
// every other screen imports it instead of writing its own toLocaleDateString.
export const leagueDateLabel = (value, now = new Date()) => {
  const date = value instanceof Date ? value : parseLeagueDate(value);
  if (!date || Number.isNaN(date.getTime())) return "";
  return isCurrentYear(date, now) ? monthDay(date) : monthDayYear(date);
};

const dayLabel = (date, now) => leagueDateLabel(date, now);

// ── When a league actually starts and ends ────────────────────────────────
//
// Client: "I tried starting a league on the 13th (12 am) and ending it on the
// 13th at 11:59 p.m.; that should be allowed as a 1-day league or 1-day game.
// When I post this one-day game under cash games or a league, it shows as ended
// right away."
//
// It did, and the reason is that `end_date` is a DATE, stored at midnight of the
// last day (`2026-09-13 00:00:00`). The backend has always read that as "the
// whole of the 13th" — sendWinnerNotification only finalises a league once
// `end_date < today`. The frontend read the same value as an INSTANT, so from
// 00:00:01 on the 13th `end < now` was true and every league spent its entire
// final day labelled "Ended". For a one-day league that is its whole life.
//
// So the end instant is late in that day, not the start of it. The hour itself
// is the client's: "I think all leagues and cash games should start at 8am end
// 8pm ET", which superseded their earlier "Every league will end at 11:59 pm".
// A value that already carries a time of day is left exactly as it is — an
// explicitly timed league must not be silently pushed to a fixed hour.
//
// Written here rather than in leagueStatus.js because four other screens parse
// these two fields by hand; they can all come through this instead.
export const parseLeagueDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // "2026-09-13 00:00:00" is not a format Safari parses; every other caller in
  // this codebase already does this swap before constructing the Date.
  let text = String(value).trim().replace(" ", "T");
  // A BARE date is a calendar day, not an instant. JS parses "2026-09-04" as
  // midnight UTC, which renders as the 3rd everywhere west of Greenwich -- the
  // create wizard's review step printed the day before the one the organiser had
  // just picked. Adding the time forces the local-midnight parse the timestamped
  // form already gets, so both spellings mean the same day.
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) text = `${text}T00:00:00`;
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
};

// True when the stored value carries no time of day, i.e. it is a plain date.
const isMidnight = (d) =>
  d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;

// The timezone a league day is measured in. The MIRROR of LEAGUE_TIMEZONE in
// the backend's config/constants.php, and it has to be, or the two halves of the
// product disagree about when the league opens and closes.
//
// Why not simply the viewer's own local clock (which is what this did first):
// the league does not run on YOUR day, it runs on the league's day. For a member
// in London or Delhi, local 8 o'clock is hours away from the Eastern 8 o'clock
// the server actually freezes the standings at, so the countdown would run out
// at the wrong moment and a finished league would still read as live — the same
// class of bug as the one this whole change fixes.
const LEAGUE_TZ = "America/New_York";

// How far the given instant's wall clock in `tz` is from UTC, in milliseconds.
// Intl is the only thing in the browser that knows the DST rules, so the answer
// is read back off a formatted date rather than assumed to be -4 or -5 hours.
const tzOffsetMs = (instant, tz) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const f = {};
  parts.forEach((p) => {
    f[p.type] = p.value;
  });
  const asIfUtc = Date.UTC(
    Number(f.year),
    Number(f.month) - 1,
    Number(f.day),
    Number(f.hour) % 24,
    Number(f.minute),
    Number(f.second)
  );
  return asIfUtc - instant.getTime();
};

// The instant at which the clock in `tz` reads `hour`:00 on this calendar day.
//
// Solved rather than assumed: the offset itself depends on the instant, so the
// first pass uses the offset at the naive guess and the second uses the offset
// at the answer. Two passes settle every case including the DST changeover,
// where the two differ by an hour.
const atHourIn = (y, m, d, hour, tz) => {
  const naive = Date.UTC(y, m, d, hour, 0, 0, 0);
  let t = naive - tzOffsetMs(new Date(naive), tz);
  t = naive - tzOffsetMs(new Date(t), tz);
  return new Date(t);
};

// The hours a league runs between, on its start_date and its end_date. MIRROR of
// LEAGUE_START_HOUR / LEAGUE_END_HOUR in the backend's config/constants.php.
//
// Client: "I think all leagues and cash games should start at 8am end 8pm ET" —
// which replaced their earlier "Every league will end at 11:59 pm". It is also
// what DetailGame.jsx has always told members ("League will start at 8:00 AM
// (EST)", finished from hour 20), so the two halves of the product now agree.
const LEAGUE_START_HOUR = 8;
const LEAGUE_END_HOUR = 20;

// A stock-only league used to close at 5 PM ET. It no longer does.
//
// LEAGUE_STOCK_END_HOUR was added on Rev4 and the client withdrew it as soon as
// they saw it running: "Oh snap, I did an all-stock league today, and the time on
// the league home page, inside the league details right-side column, changed to
// 5pm... I think we should keep All Stocks at 8:00 AM to 8:00 PM ET as well. The
// stock market will already be closed for the final three hours, so there will be
// no stock trading during that time, but the competition itself can still
// officially end at 8:00 PM."
//
// So the constant, the isStockOnlyLeague() test and the branch in leagueEndAt are
// all gone. Every competition now runs 8 AM to 8 PM ET, which is one rule instead
// of two and is what every hard-coded label on the landing pages already said.

// A stored bare date anchored to its league hour in the league's own timezone.
//
// The Y/M/D are read off the stored value as the SERVER wrote them, which is
// why parseLeagueDate's local parse is used and not getUTCFullYear: the date
// "2026-09-13" means the thirteenth, not an instant to be converted.
//
// A value that already carries a time of day is returned untouched — an
// explicitly timed league must not be silently pushed to a fixed hour.
const atLeagueHour = (value, hour) => {
  const d = parseLeagueDate(value);
  if (!d) return null;
  if (!isMidnight(d)) return d;
  try {
    return atHourIn(d.getFullYear(), d.getMonth(), d.getDate(), hour, LEAGUE_TZ);
  } catch (e) {
    // Intl without full timezone data (very old browsers) — fall back to the
    // viewer's own clock rather than returning nothing, which for the end
    // instant would make the league immortal.
    const out = new Date(d);
    out.setHours(hour, 0, 0, 0);
    return out;
  }
};

// 8:00 AM ET on start_date — the instant the league opens and trading unlocks,
// the same instant the backend gates on (App\Support\LeagueAssets::startsAt).
export const leagueStartAt = (game) => atLeagueHour(game?.start_date, LEAGUE_START_HOUR);

// The last instant the league is live, and the same instant the backend takes
// its final mark and freezes the standings on (App\Support\LeagueAssets::endsAt).
//
// 8:00 PM ET, for every competition. Everything that prints or counts down to
// the end (endMomentLabel, getLeagueStatus, the ENDS row on both landing pages)
// reads this one function.
export const leagueEndAt = (game) => atLeagueHour(game?.end_date, LEAGUE_END_HOUR);

// How many calendar days a league runs for, inclusive of its last day.
//
// This used to be `round((end - start) / 1 day)` against the two raw values, and
// for a one-day league — start and end on the same date, both stored at midnight
// — that is zero, so both "1-Day League" and "1 day" came back null and the
// league's own cards printed no duration at all. Measuring to the END instant
// makes the same subtraction come to 23h 59m, which rounds to the 1 the client
// is asking for.
const leagueDayCount = (game) => {
  const start = leagueStartAt(game);
  const end = leagueEndAt(game);
  if (!start || !end) return null;
  const ms = end - start;
  if (Number.isNaN(ms) || ms <= 0) return null;
  return Math.max(1, Math.round(ms / (24 * 60 * 60 * 1000)));
};

// "Completed · Sep 12" — the completed leaderboard's status line. The year
// follows leagueDateLabel's rule rather than always being printed: the client
// asked for it off inside the current year, and a league finished this year is
// the overwhelmingly common case on this screen.
// Which LEAGUE day an instant falls on, as a plain date the label helpers can
// format.
//
// A label that names a day must name the league's day, not the viewer's. The end
// instant is 8:00 PM ET, which is already the NEXT calendar day in UTC and
// everywhere east of it -- so formatting it straight off the viewer's clock
// printed "Completed \u00b7 Sep 13" in London and Delhi for a league that finished on
// the 12th. Same reasoning as LEAGUE_TZ above, applied to the printed date
// rather than the countdown.
const leagueDayOf = (instant) => {
  if (!instant || Number.isNaN(instant.getTime())) return null;
  try {
    const f = {};
    new Intl.DateTimeFormat("en-US", {
      timeZone: LEAGUE_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(instant)
      .forEach((p) => {
        f[p.type] = p.value;
      });
    return new Date(Number(f.year), Number(f.month) - 1, Number(f.day));
  } catch (e) {
    return instant;
  }
};

export const completedOnLabel = (game, now = new Date()) => {
  const end = leagueDayOf(leagueEndAt(game));
  if (!end) return "Completed";
  return `Completed \u00b7 ${leagueDateLabel(end, now)}`;
};

// "Finalized Sep 12" — replaces "Updated daily" on a completed league's
// profile, where the figures are frozen rather than refreshed.
export const finalizedOnLabel = (game, now = new Date()) => {
  const at = leagueDayOf(parseLeagueDate(game?.finalized_at) || leagueEndAt(game));
  if (!at) return "Finalized";
  return `Finalized ${leagueDateLabel(at, now)}`;
};

// THE range form, spelled "Sep 4 to Sep 12". One joiner, everywhere: the word
// "to" is what the app already used before the redesign (the create wizard's
// review step), and the en dash was the mockup's. The client asked for the
// former — "Stick to our format with dates. Don't let us drift because of the
// stitch design drifting".
//
// Years follow leagueDateLabel's rule. When both ends sit inside one past year
// the year is stated once, at the end of the range ("Oct 1 to Oct 31, 2025"); a
// range that crosses a year boundary states both ("Dec 28, 2025 to Jan 5, 2026").
// Same-month ranges keep both month labels, so a range never reads as one date.
const JOINER = " to ";

const rangeLabel = (start, end, joiner, now) => {
  if (isCurrentYear(start, now) && isCurrentYear(end, now)) {
    return `${monthDay(start)}${joiner}${monthDay(end)}`;
  }
  if (start.getFullYear() === end.getFullYear()) {
    return `${monthDay(start)}${joiner}${monthDayYear(end)}`;
  }
  return `${monthDayYear(start)}${joiner}${monthDayYear(end)}`;
};

// Two stored values (not a game row) as a range — for the create wizard, which
// is formatting the dates in the form rather than on a saved league.
export const leagueDateRange = (startValue, endValue, now = new Date()) => {
  const start = parseLeagueDate(startValue);
  const end = parseLeagueDate(endValue);
  if (start && end) return rangeLabel(start, end, JOINER, now);
  if (start) return leagueDateLabel(start, now);
  if (end) return leagueDateLabel(end, now);
  return "";
};

// A live league's date range.
export const dateRangeLabel = (game, now = new Date()) => {
  const start = parseLeagueDate(game?.start_date);
  const end = parseLeagueDate(game?.end_date);
  if (start && end) return rangeLabel(start, end, JOINER, now);
  if (start) return leagueDateLabel(start, now);
  if (end) return `Ends ${leagueDateLabel(end, now)}`;
  return "";
};

// Asset mix label ("Stocks", "Crypto", "Mixed"). asset_category is collected by
// the create wizards but is not persisted yet, so this returns "" for most
// leagues and callers must drop the segment rather than print a placeholder.
export const assetLabel = (game) => {
  const raw = game?.asset_category || game?.asset_type || "";
  if (!raw) return "";
  const key = String(raw).toLowerCase();
  if (key === "mixed" || key === "mix") return "Mixed";
  if (key === "forex" || key === "fx") return "Forex";
  return key.charAt(0).toUpperCase() + key.slice(1);
};

// What KIND of competition this is, for the type line on a league card. A cash
// game is a cash game first — its funding matters more than whether it's open to
// everyone — so that check comes before public/private.
export const leagueTypeKey = (game) => {
  if (game?.is_cash_prize_league) return "cash";
  return game?.game_type === "private" ? "private" : "public";
};

export const LEAGUE_TYPE_LABELS = {
  cash: "Cash Game",
  private: "Private League",
  public: "Public League",
};

export const leagueTypeLabel = (game) => LEAGUE_TYPE_LABELS[leagueTypeKey(game)];

// "Public" / "Private" / "Cash" — for a labelled row that already says LEAGUE
// TYPE, where repeating the word "League" in the value just reads as an echo.
export const leagueTypeShort = (game) =>
  (leagueTypeLabel(game) || "").replace(/\s*League$/i, "");

// ── the competition's own vocabulary ──────────────────────────────────────
//
// Client 0014889 listed seventeen strings that read "league" on a Cash Game
// screen and should read "game" or "entrant": "These are all simple conditional
// copy changes, not new functionality."
//
// One map rather than a ternary at each call site. The point is not brevity —
// it is that the next competition kind is a row added here, not another sweep
// through nine files hunting for the word "league". It also means a screen
// cannot end up half-translated, which is exactly how "Hidden during game"
// already existed on the landing page while the in-league rail still said
// "Hidden during league".
//
// NOT in this map: "Switch competition". The client made that one
// unconditional — "It covers cash games, public leagues, and private leagues
// without changing the destination" — so it is a plain string at its call site,
// not a variant.
const LEAGUE_WORDS = {
  kind: "LEAGUE",
  createdBy: "Created by",
  memberNoun: "member",
  memberNounPlural: "members",
  feedTitle: "League feed",
  composerPlaceholder: "Share a chart, trade idea, or update with the league\u2026",
  settings: "League settings",
  details: "League details",
  leadingLine: "You're leading the league.",
  // Client 0014889, confirmed as global: "Competition type is the best
  // scalable label because the same field can show Cash Game, Public League,
  // or Private League." One label for all three, so the row reads the same
  // whichever kind the member is standing in.
  typeRowLabel: "Competition type",
  membersRowLabel: "Members",
  joinedSuffix: "joined",
  joinAfterStartLabel: "Join after start",
  recordLine: "League results don't affect your main record.",
  dashboard: "League Dashboard",
};

const CASH_GAME_WORDS = {
  ...LEAGUE_WORDS,
  kind: "CASH GAME",
  createdBy: "Hosted by",
  memberNoun: "entrant",
  memberNounPlural: "entrants",
  feedTitle: "Game feed",
  composerPlaceholder: "Share a chart, trade idea, or game update\u2026",
  settings: "Game settings",
  details: "Game details",
  leadingLine: "You're leading the game.",
  membersRowLabel: "Entrants",
  joinedSuffix: "entered",
  joinAfterStartLabel: "Entry after start",
  recordLine: "Game results don't affect your main record.",
  dashboard: "Game Dashboard",
};

export const competitionCopy = (game) =>
  leagueTypeKey(game) === "cash" ? CASH_GAME_WORDS : LEAGUE_WORDS;

// "3 members" / "3 entrants" — the count with the right noun for this
// competition, pluralised.
export const memberCountLabel = (game, count) => {
  const words = competitionCopy(game);
  return `${count} ${count === 1 ? words.memberNoun : words.memberNounPlural}`;
};

// "Oct 1 to Oct 31" (or "Oct 1 to Oct 31, 2025") — the historical form, for a
// league that has already run.
// Spelled "to" rather than the en-dash the live cards use: this is a record of
// when it happened, not a countdown you're reading against.
export const historyRangeLabel = (game, now = new Date()) => {
  const start = game?.start_date ? new Date(game.start_date) : null;
  const end = game?.end_date ? new Date(game.end_date) : null;
  const startOk = start && !Number.isNaN(start.getTime());
  const endOk = end && !Number.isNaN(end.getTime());
  if (startOk && endOk) return rangeLabel(start, end, JOINER, now);
  if (startOk) return dayLabel(start, now);
  if (endOk) return dayLabel(end, now);
  return "";
};

// The user's own finishing numbers. Both are only rendered when the payload
// actually carries them — get-all-my-games returns plain league rows today, so
// most leagues show the card without invented figures rather than a fake "#1".
export const finalRank = (game) => {
  const raw =
    game?.my_rank ?? game?.user_rank ?? game?.rank ?? game?.final_rank ?? game?.standing;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const finalReturn = (game) => {
  const raw =
    game?.my_performance ??
    game?.performance ??
    game?.final_performance ??
    game?.avg_performance;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

// A return, rounded to one decimal, as the whole site prints it.
//
// The rounding happens BEFORE the sign is chosen, which is the whole point: a
// raw -0.04 rounds to 0.0, and signing first produced "-0.0%" — a minus sign on
// a number that isn't negative. Same for "+0.0%".
//
// A missing value is NOT zero: Number(null) is 0, which would print a confident
// "0.0%" for a league that simply hasn't reported a return.
const roundedReturn = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  // Number() strips the sign from "-0.0", so a rounds-to-zero value compares
  // as neither positive nor negative below.
  return Number(n.toFixed(1));
};

export const formatReturn = (value) => {
  const n = roundedReturn(value);
  if (n === null) return "";
  if (n > 0) return `+${n.toFixed(1)}%`;
  if (n < 0) return `${n.toFixed(1)}%`;
  return "0.0%";
};

// A return as text AND colour, from one decision, so the two can never
// disagree — the bug the client caught was a card hard-coding green next to a
// negative number.
//
//   positive   green, with a + sign
//   negative   red, with a - sign
//   0.0%       grey, no sign at all
//
// Returns null when there is no figure to show, so callers drop the line rather
// than printing a 0.0% nobody achieved.
export const returnDisplay = (value) => {
  const n = roundedReturn(value);
  if (n === null) return null;
  if (n > 0) return { text: formatReturn(value), cls: "text-[#35B23C]" };
  if (n < 0) return { text: formatReturn(value), cls: "text-red-500" };
  return { text: "0.0%", cls: "text-secondary" };
};

// "30 days" — how long the league runs, for the stat strips.
//
// Client Rev 2: "Duration: Use 30 days. Not 30 Days, 30d, or 30-Days with
// punctuation between the number and word. The value under the DURATION micro
// label should be things like 1 day, 7 days, 30 days. Also, I would get rid of
// <1 Week entirely. If the league is six days, say 6 days."
//
// So: DAYS, lowercase unit, a plain space between number and word, and no
// "<1 Week" bucket at all — a six-day league says "6 days" rather than being
// rounded into a week it does not fill. Anything under a full day floors to
// "1 day"; a league that short still ran for a day from the reader's point of
// view, and "0 days" states nothing.
// Returns null when either date is missing so callers can drop the stat instead
// of printing a dash.
export const durationLabel = (game) => {
  const days = leagueDayCount(game);
  return days ? `${days} day${days === 1 ? "" : "s"}` : null;
};

// What's tradable, as a stat: the count when the league is restricted to a list,
// otherwise "All <category>". asset_category has no backend column yet (see
// assetLabel), so unrestricted leagues read "All assets" until it lands.
export const assetTypeLabel = (game) => {
  const symbols = Array.isArray(game?.asset_symbols) ? game.asset_symbols : [];
  if (symbols.length) return `${symbols.length} asset${symbols.length === 1 ? "" : "s"}`;
  const category = assetLabel(game);
  if (!category || category === "Mixed") return "All assets";
  return `All ${category.toLowerCase()}`;
};

// Client Rev 2, private league landing. The ASSET TYPE stat said "7 assets" for
// every league, whatever was actually in it. The client wants the unit to name
// the thing:
//
//   MOBILE  — "the selected quantity and asset unit … for example 7 coins,
//             12 stocks, 5 pairs, or 6 assets (if mixed is chosen)"
//   DESKTOP — "the category plus selected quantity exactly like this:
//             Crypto · 7 coins , Stocks · 7 selected , Forex · 5 pairs ,
//             Mixed · 7 assets"
//
// Note the deliberate difference on stocks: mobile says "12 stocks", desktop says
// "7 selected" — because desktop already prints "Stocks ·" in front of it and
// "Stocks · 7 stocks" would say the word twice. Those are the client's own two
// lists, transcribed.
//
// And, both breakpoints: "If the full asset category was selected rather than
// Custom, show Crypto, Stocks, or Forex." A Custom selection is what fills
// asset_symbols; an empty list means the whole category.
const ASSET_UNITS = {
  Crypto: { mobile: "coins", desktop: "coins" },
  Stocks: { mobile: "stocks", desktop: "selected" },
  Forex: { mobile: "pairs", desktop: "pairs" },
  Mixed: { mobile: "assets", desktop: "assets" },
};

export const assetSelectionLabel = (game, variant = "mobile") => {
  const symbols = Array.isArray(game?.asset_symbols)
    ? game.asset_symbols.filter(Boolean)
    : [];
  const category = assetLabel(game);

  // Full category, not Custom — the category name says everything.
  if (!symbols.length) return category || "All assets";

  const unit = (ASSET_UNITS[category] || ASSET_UNITS.Mixed)[
    variant === "desktop" ? "desktop" : "mobile"
  ];
  const count = `${symbols.length} ${unit}`;
  // Mobile shows the quantity alone; desktop prefixes the category.
  return variant === "desktop" && category ? `${category} · ${count}` : count;
};

// "Ended Aug 8" — a finished league states WHEN, not just that it's over. Only
// the listing needs this; getLeagueStatus keeps its bare "Ended" label for the
// pills and countdown strips, which have no room for a date.
export const endedOnLabel = (game, now = new Date()) => {
  const end = game?.end_date ? new Date(game.end_date) : null;
  if (!end || Number.isNaN(end.getTime())) return "";
  return `Ended ${dayLabel(end, now)}`;
};

// "7-Day League" — the browse list's own phrasing. Both this and durationLabel now
// measure in days (client Rev 2); they differ only in wording, because the list is
// naming a KIND of league ("7-Day League") while the stat strip is stating a fact
// about this one ("7 days").
export const leagueLengthLabel = (game) => {
  const days = leagueDayCount(game);
  return days ? `${days}-Day League` : null;
};

// "Beginner" / "All levels" — the room a public league says it's setting up.
//
// NOTE: `experience_level` has no backend column yet (the create wizard sends it
// so it persists the day one lands). Until then this returns null for real
// leagues, and metaLine simply drops the segment.
const EXPERIENCE_LABELS = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
  all: "All levels",
};

export const experienceLabel = (game) =>
  EXPERIENCE_LABELS[String(game?.experience_level || "").toLowerCase()] || null;

// Joins the segments a tile has with the "·" separator, skipping the blanks.
export const metaLine = (...parts) => parts.filter(Boolean).join(" · ");

// Whether a league is still taking members once it has started, in the short
// form the preview cards use: "Open to join" / "Entry closed".
//
// This is the SAME `join_after_start` setting the wizard describes in sentences
// (joinAfterStartLabel), said in two words because a card's metadata line has no
// room for a sentence. The wizard's wording is unchanged.
export const joinStateLabel = (game) =>
  game?.join_after_start ? "Open to join" : "Entry closed";

// The metadata line under a preview card's second line, split into the two
// weights the design gives it.
//
//   Public,  Starting Soon   Advanced · Forex · 4-Day League
//   Public,  Live Now        Advanced · Forex · 4-Day League · Entry closed
//   Private, Starting Soon   Private League · Forex · 4-Day League
//   Private, Live Now        Private League · Forex · 4-Day League
//
// Two rules the client set, and neither is about which screen you are on — a
// league reads the same everywhere:
//
//   • a PRIVATE league leads with "Private League" where a public one leads with
//     its experience level, because private leagues are never asked who they are
//     pitched at
//   • joinability appears on PUBLIC live leagues only: it is a public league's
//     open door, and it is the one segment that stays visually quiet
//
// Returned as parts, not one string, because `join` is styled a step lighter
// than `primary` and a single string cannot carry two weights. There is
// deliberately no member count — the design has none.
export const previewMetaParts = (game, status) => {
  const isPrivate = game?.game_type === "private";
  const lead = isPrivate ? LEAGUE_TYPE_LABELS.private : experienceLabel(game);
  const live = status?.tone === "live";

  return {
    primary: metaLine(lead, assetLabel(game), leagueLengthLabel(game)),
    join: live && !isPrivate ? joinStateLabel(game) : "",
  };
};

// "7-Day Game" — leagueLengthLabel's wording for a Cash Game.
//
// Client, on the Cash Games View All cards: "Metadata example: Prize Pool $100 •
// Crypto • 7-Day Game. Game replaces League". Same measurement as the league
// form, one word different, so a cash game is never described as a league on a
// screen that calls it a game.
export const gameLengthLabel = (game) => {
  const label = leagueLengthLabel(game);
  return label ? label.replace(/League$/, "Game") : null;
};

// The metadata line on a Cash Game preview row.
//
//   Prize Pool $100 · Crypto · 7-Day Game
//
// Client: "Replace Experience level in the metadata with the dynamic Prize Pool
// amount... The prize amount must pull from the existing Total Prize Pool field.
// It should not be hardcoded." So the lead segment is formatPrize of
// `cash_prize_amount` and nothing else — a cash game is never asked what
// experience level it is pitched at.
//
// Returned in the same { primary, join } shape as previewMetaParts so the row
// component can render either kind without branching on the styling. There is no
// `join` segment: every cash game closes entry at the start (the client's own
// "JOIN AFTER START: Not allowed"), so printing it on every row would be noise.
export const cashMetaParts = (game) => {
  const prize = formatPrize(game?.cash_prize_amount);
  return {
    primary: metaLine(
      prize ? `Prize Pool ${prize}` : "",
      assetLabel(game),
      gameLengthLabel(game)
    ),
    join: "",
  };
};

// The podium payout rows a Cash Game landing page prints, top prize first.
//
// First place is the large TOP PRIZE figure and is always present on a published
// cash game (the API will not accept one without it). Second and third are
// optional, and the client's rule is explicit: "If an optional payout is blank or
// zero, do not display that payout row on the landing page." formatPrize already
// returns null for blank, zero and negative, so filtering on its result is the
// same test — one definition of "is there a prize here", not two that can drift.
//
// `rail` is the colour of the small bar beside each placing in the design:
// silver for second, bronze for third. Those are the MOBILE design's values;
// the desktop mock uses flatter greys, and the client said the desktop file "is
// just a visual layout using the mobile elements", so mobile wins.
export const podiumPayouts = (game) => {
  const rows = [
    { key: "second", label: "2nd Place", amount: game?.second_place_amount, rail: "#C0C0C0" },
    { key: "third", label: "3rd Place", amount: game?.third_place_amount, rail: "#CD7F32" },
  ];
  return rows
    .map((row) => ({ ...row, display: formatPrize(row.amount) }))
    .filter((row) => !!row.display);
};

// The supporting line under the asset name on the Cash Game landing page —
// the design's third line, "All major U.S. stocks and crypto".
//
// The client's field mapping defines the line ABOVE this one (the existing
// asset selection) and says nothing about this one, so it is not driven by a
// field. It cannot be a fixed string either: the design's copy names stocks and
// crypto, which would be wrong printed under a Forex or crypto-only game.
//
// So it is derived from the category the organiser actually picked, following
// the client's own phrasing. THE WORDING IS OURS, not theirs — it is the one
// piece of copy on this page they have not supplied, and it is flagged to them
// for replacement.
//
// A CUSTOM selection returns null and the line is dropped: line two already
// reads "7 coins", which is more specific than any "all major …" claim, and
// saying "all" above a restricted list would contradict it.
const ASSET_SCOPE_NOTES = {
  Stocks: "All major U.S. stocks",
  Crypto: "All major crypto",
  Forex: "All major forex pairs",
  Mixed: "All major U.S. stocks and crypto",
};

export const assetScopeNote = (game) => {
  const symbols = Array.isArray(game?.asset_symbols)
    ? game.asset_symbols.filter(Boolean)
    : [];
  if (symbols.length) return null;
  return ASSET_SCOPE_NOTES[assetLabel(game)] || null;
};

// The TOP PRIZE figure: 1st place amount when the game has one.
//
// Falls back to the total prize pool for a cash game published BEFORE the podium
// columns existed — those rows have a pool and three nulls, and showing the pool
// is both true and the only number there is. New games cannot reach the fallback:
// first place is required at creation.
export const topPrizeDisplay = (game) =>
  formatPrize(game?.first_place_amount) || formatPrize(game?.cash_prize_amount);

// The organiser's HANDLE. QUSHi never displays a user's real name, so there is
// deliberately no full_name fallback here — a missing handle falls through to
// the platform name rather than exposing who the person is.
export const organizerHandle = (game) => game?.user?.user_name || "Qushi";

// The second line of a preview card — what the league is selling before it
// starts, and what it is producing once it has:
//
//   Starting Soon   its tagline
//   Live Now        "Leading return +18.7%"
//   Completed       "Winning return +42.8%"
//
// Both figures come from the same place (the top of the league's board); only
// the label changes, because a lead is provisional and a win is not. Returns
// null when there is nothing to say, so the card drops the line rather than
// printing a 0.0% nobody achieved.
export const previewSecondLine = (game, status) => {
  const tone = status?.tone;
  if (tone === "live" || tone === "ended") {
    const display = returnDisplay(game?.leading_performance);
    if (!display) return null;
    return {
      label: tone === "live" ? "Leading return" : "Winning return",
      value: display.text,
      valueClass: display.cls,
    };
  }
  const tagline = previewTagline(game);
  return tagline ? { tagline } : null;
};

// The one-line hook shown on a preview card. NEVER the description — that field
// belongs to the league's own screens and is far too long for a card.
export const previewTagline = (game) => String(game?.tagline || "").trim();

// Toggle results. These four strings are fixed by the design notes and are the
// ONLY wording either setting gets — the create wizard, Review & Create, and
// every private or public league screen read them from here so a league can't
// describe its own rules two different ways.
export const joinAfterStartLabel = (joinAfterStart) =>
  joinAfterStart ? "Late entries allowed" : "Join before the league starts";

// Takes the raw `private_portfolio` flag: true means portfolios are HIDDEN.
//
// Client Rev 3: "Portfolio Visibility toggle. The current line wraps one word on
// landing pages. Please replace our toggle on/off message with these site-wide:
// Visible during league / Hidden during league."
//
// "Site-wide" is why the long and short forms below now return the SAME two
// strings: they were "Portfolios hidden until the league ends" / "Live
// portfolios visible to all members" and "Hidden until league ends" / "Visible
// to all members" — four wordings for one setting, and the long pair is what
// wrapped. Both exports stay so no caller has to change, and both resolve here,
// which is what makes "site-wide" true rather than a thing to remember.
const PORTFOLIO_VISIBILITY = {
  hidden: "Hidden during league",
  visible: "Visible during league",
};

export const portfolioVisibilityLabel = (privatePortfolio) =>
  privatePortfolio ? PORTFOLIO_VISIBILITY.hidden : PORTFOLIO_VISIBILITY.visible;

// Short forms of the same two settings, for a labelled list where the row already
// names the subject ("JOIN AFTER START: Not allowed"). Same source of truth as the
// sentences above, so a league still can't describe a rule two different ways —
// only the sentence's subject moves into the label.
export const joinAfterStartShort = (joinAfterStart) =>
  joinAfterStart ? "Allowed" : "Not allowed";

export const portfolioVisibilityShort = portfolioVisibilityLabel;

// ENTRY, on a Cash Game landing page. Client: "Contest Rules: Replace
// `JOIN AFTER START: Not allowed` with `ENTRY: Entry closes when the game
// starts.`"
//
// A sentence rather than the two-word short form, because that is what they
// wrote — and it answers the question the old row raised without answering
// ("not allowed" to do what, and from when?).
//
// Still DERIVED from the setting, not fixed: a league whose organiser allowed
// late entry would be lied to by the client's sentence. The open case keeps the
// product's existing site-wide wording for it rather than inventing a second
// sentence, so only the case the client specified is new.
export const entryRuleLabel = (joinAfterStart) =>
  joinAfterStart ? joinAfterStartLabel(true) : "Entry closes when the game starts.";

// The same setting, in a Cash Game's wording: "Hidden during game".
//
// The client asked for exactly that on the Cash Game landing page —
// "PORTFOLIO VISIBILITY: Hidden during game" — and it is not a second opinion
// about the setting, it is the one word that changes on every cash game surface.
// They set that rule themselves for the metadata line ("Game replaces League"),
// and gameLengthLabel already applies it to the duration.
//
// Derived from the site-wide string rather than written out again, so the two
// cannot drift: whatever the shared label says, this is that sentence with its
// last word swapped. The site-wide strings above are untouched — every league
// screen still reads "during league".
export const portfolioVisibilityGameShort = (privatePortfolio) =>
  portfolioVisibilityLabel(privatePortfolio).replace(/league$/i, "game");

// THE time rule for the whole product, alongside leagueDateLabel above.
//
// Client: "I think we should change our token standard for time to: Sep 17 · 8
// AM ET vs. Sep 17 · 8:00 AM ET, especially on mobile." Then, revising their own
// first thought that desktop could keep the ":00": "do not drift between desktop
// and mobile — all league play times always start and end on the hour, so make
// this the consistent rule: Sep 17 · 8 AM ET. For QUSHi's application surfaces,
// use the cleaner format everywhere. Desktop should match mobile."
//
// So there is ONE string at every width. ":00" is dropped because on the hour it
// carries no information — but only when the minutes really are zero. A league
// stored at 8:30 still prints "8:30 AM": the client's rule rests on "always start
// and end on the hour", and silently rounding a time that isn't would turn a
// tidier label into a wrong one.
const hourClock = (instant, opts) => {
  const time = instant.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...opts,
  });
  // "8:00 AM" -> "8 AM". Anchored on ":00" immediately before the space so a
  // real 8:30 or 10:05 is left alone.
  return time.replace(/:00(?=\s)/, "");
};

// "Sep 17 · 8 AM ET" — an exact instant ON THE LEAGUE'S OWN CLOCK. The landing
// pages and the details card are the places the exact moment matters: it's what
// someone is deciding to show up for.
//
// This used to format in the READER's timezone. Client: "STARTS: Sep 17 · 8:00 AM
// ET / ENDS: Sep 19 · 8:00 PM ET" — they wrote ET. In the reader's clock the same
// row came out "Sep 17 · 5:30 PM GMT+5:30" for a viewer in India: arithmetically
// the same instant, but not the fact the client asked to be shown.
//
// Formatting the DAY in LEAGUE_TZ matters as much as the time. A league whose
// last day is the 18th ends at 20:00 ET on the 18th, which is 05:30 on the 19th
// in Delhi — so the reader's clock would name a day the league does not run on.
//
// NOTE, and flagged to the client: this prints month and day only, so it does
// NOT pick up leagueDateLabel's rule of showing the year once the date is not in
// the current year. They asked about that rule as a question and said no change
// yet, so it is left as it is rather than quietly rewired.
const momentLabel = (instant) => {
  if (!instant || Number.isNaN(instant.getTime())) return "";
  const opts = { timeZone: LEAGUE_TZ };
  try {
    const day = instant.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...opts,
    });
    // "ET" rather than the short zone name, so the label reads the same in March
    // and in December instead of flipping between EDT and EST. It is also the
    // client's own wording.
    return `${day} · ${hourClock(instant, opts)} ET`;
  } catch (e) {
    // Intl without full timezone data (very old browsers). Fall back to the
    // viewer's clock WITH its own zone name, so the label is still true about
    // which instant it names rather than claiming an Eastern time it did not
    // compute.
    const day = instant.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${day} · ${hourClock(instant, { timeZoneName: "short" })}`;
  }
};

// Client: "STARTS: Sep 17 · 8 AM ET / ENDS: Sep 19 · 8 PM ET".
//
// The 8 AM / 8 PM ET is not a choice made here — it is what leagueStartAt /
// leagueEndAt already resolve a league's dates to, and the same instant the
// backend gates on (App\Support\LeagueAssets).
//
// ONE pair of functions for every surface. The details card briefly had its own
// longer format ("Sep 17, 2026 at 8:00 AM ET") because the client wrote it that
// way for that section; they then asked for one standard everywhere — "Keep us
// consistent always" — so the second format is gone rather than maintained.
export const startMomentLabel = (game) => momentLabel(leagueStartAt(game));

export const endMomentLabel = (game) => momentLabel(leagueEndAt(game));


// ── Starting cash and trade limit ─────────────────────────────────────────
//
// Client, for the public league landing page and then again for the private
// one: "Please add Starting cash and Trade limit to the existing league details
// card... • STARTING CASH $100,000 virtual cash • TRADE LIMIT Unlimited trades.
// Use the actual dynamic values where applicable."
//
// NOTHING IS DYNAMIC HERE YET, and that is a fact about the product rather than
// a shortcut. Every competition issues the same virtual balance — the backend
// hands each member 100,000 on enrolment (GameController::enrolMember) and there
// is no column on `games` for a different figure — and no competition caps how
// many trades a member may place. So these read as the two constants they are.
//
// They live here rather than in each landing page because THREE surfaces print
// them now: the public landing card, the private landing card, and the in-league
// details rail. The Cash Game landing page has carried its own pair of these
// strings since it was built, as part of the block of static copy the client
// signed off there ("The following Cash Game information can remain static for
// launch"); it is left exactly as it is, and the two must be changed together if
// the figure ever moves.
//
// When a per-league starting balance does arrive, it arrives in these two
// functions and every screen picks it up at once.
export const startingCashLabel = () => "$100,000 virtual cash";
export const tradeLimitLabel = () => "Unlimited trades";
